export interface ApiError {
	message: string;
	code?: string;
}

export class ApiException extends Error {
	code?: string;
	status: number;
	constructor(message: string, status: number, code?: string) {
		super(message);
		this.name = "ApiException";
		this.status = status;
		this.code = code;
	}
}

const API_BASE = "/api";

let shareToken: string | null = null;

export function setShareToken(token: string | null) {
	shareToken = token && token.trim() ? token.trim() : null;
}

export function getShareToken(): string | null {
	return shareToken;
}

function withShareToken(path: string): string {
	if (!shareToken) return path;
	const sep = path.includes("?") ? "&" : "?";
	return `${path}${sep}t=${encodeURIComponent(shareToken)}`;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
	const headers = new Headers(options.headers);
	if (options.body !== undefined && !headers.has("Content-Type")) {
		headers.set("Content-Type", "application/json");
	}

	const res = await fetch(`${API_BASE}${withShareToken(path)}`, {
		credentials: "same-origin",
		...options,
		headers,
	});

	const body = await res.json().catch(() => ({} as Record<string, unknown>));

	if (!res.ok) {
		const nested = (body as { error?: ApiError }).error;
		const flat = body as { message?: string; code?: string };
		throw new ApiException(
			nested?.message ?? flat.message ?? "Something went wrong",
			res.status,
			nested?.code ?? flat.code,
		);
	}

	return body as T;
}

export const api = {
	get: <T>(path: string) => request<T>(path, { method: "GET" }),
	post: <T>(path: string, data?: unknown) =>
		request<T>(path, {
			method: "POST",
			body: data !== undefined ? JSON.stringify(data) : undefined,
		}),
	patch: <T>(path: string, data?: unknown) =>
		request<T>(path, {
			method: "PATCH",
			body: data !== undefined ? JSON.stringify(data) : undefined,
		}),
	del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
